import { faArrowUpRight } from '@fortawesome/pro-light-svg-icons';
import {
  faEnvelope,
  faEye,
  faEyeSlash,
  faLock,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import qs from 'qs';
import React, { useState } from 'react';
import { get, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import RealBrandLogo from '../assets/img/real-brand-logo-black-border.svg';
import Button from '../components/Button.tsx';
import AuthPageLayout from '../components/Layout/AuthPageLayout.tsx';
import ControlledTextInput from '../components/inputs/ControlledTextInput.tsx';
import useQueryParams from '../hooks/useQueryParams.ts';
import { JwtAuthenticationResponse } from '../openapi/keymaker/index.ts';
import ErrorService from '../services/ErrorService.ts';
import { fetchAuthUserDetail, loginUser } from '../slices/AuthSlice.ts';
import { useAppDispatch } from '../slices/store.ts';
import { AnalyticsEventEnum, ErrorCode } from '../types.ts';
import { getErrorMessage } from '../utils/ErrorUtils.ts';
import { USERNAME_OR_EMAIL_VALIDATIONS } from '../utils/Validations.tsx';
import { cn } from '../utils/classUtils.ts';
import { getPathWithQueryParams } from '../utils/commonUtils.ts';

interface SignInRouteProps {}

interface Query {
  leadId?: string;
  loanId?: string;
  emailAddress?: string;
  redirectTo?: string;
  inviterAuthorizedPersonId?: string;
  inviterAgentId?: string;
  loanOfficerId?: string;
}

interface FormData {
  usernameOrEmail: string;
  password: string;
  submit: string;
}

const SignInRoute: React.FC<SignInRouteProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    leadId,
    loanId,
    emailAddress,
    redirectTo,
    inviterAuthorizedPersonId,
    inviterAgentId,
    loanOfficerId,
  } = useQueryParams<Query>();
  const [hidePassword, setHidePassword] = useState(true);
  const location = useLocation();

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: { usernameOrEmail: emailAddress as string },
  });
  const isAgentLogin = location.pathname === '/agent-login';
  const isGenericLogin = location.pathname === '/login';

  const handleSignInRedirect = async (data: JwtAuthenticationResponse) => {
    if (data?.userBlockedUntilEmailVerified) {
      navigate(isAgentLogin ? '/agent-verify-email' : '/verify-email');
    } else if (data?.forceMfa && !data?.mfaType) {
      navigate(
        isAgentLogin
          ? `/agent-enable-mfa?${qs.stringify({ leadId, redirectTo })}`
          : `/enable-mfa?${qs.stringify({ leadId, redirectTo })}`,
      );
    } else if (data?.mfaType) {
      navigate(
        isAgentLogin
          ? `/agent-login/mfa?${qs.stringify({ leadId, redirectTo })}`
          : `/login/mfa?${qs.stringify({ leadId, redirectTo })}`,
      );
    } else {
      const { isLoginSuccess } = await dispatch(
        fetchAuthUserDetail(leadId as string),
      );

      if (!isLoginSuccess) {
        setError('submit', {
          message:
            'Looks like the account you are trying to login as is an agent account. Please login to the ReZEN app instead.',
        });
      } else {
        navigate('/', { replace: true });
      }
    }
  };

  const onSubmit = async (values: FormData) => {
    try {
      const data = await dispatch(loginUser(values));
      await handleSignInRedirect(data!);
    } catch (e: any) {
      setError('submit', {
        message:
          e.response?.data?.errorMessage ||
          getErrorMessage(ErrorService.getErrorCode(e), 'login'),
      });
      const errorCode = ErrorService.getErrorCode(e);
      if (errorCode !== ErrorCode.UNAUTHORIZED) {
        ErrorService.notify('unable to login', e, { meta: { ...values } });
      }
    }
  };

  return (
    <AuthPageLayout
      eventName={AnalyticsEventEnum.LOGIN_SCREEN_VIEWED}
      showRightSection={isAgentLogin}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={isAgentLogin ? 'w-full lg:w-4/5' : 'w-full max-w-md'}
      >
        {(isAgentLogin || isGenericLogin) && (
          <div
            className={cn(
              'lg:flex hidden pb-32',
              isGenericLogin && 'justify-center',
            )}
          >
            <img src={RealBrandLogo} alt='placeholder' width={90} height={50} />
          </div>
        )}
        <div className='pb-5 lg:pb-14'>
          <p
            className={`font-poppins-medium lg:text-5xl text-2xl text-reskin-primary-dark ${
              isAgentLogin ? 'text-left' : 'text-center'
            }`}
          >
            Login
          </p>
        </div>
        {get(errors, 'submit') && (
          <div className='mb-5 flex bg-primary-coral rounded-lg p-2'>
            <p className='font-inter-medium text-white text-base'>
              {get(errors, 'submit').message}
            </p>
          </div>
        )}
        <div className='pb-4 lg:pb-6'>
          <ControlledTextInput<FormData, 'usernameOrEmail'>
            name='usernameOrEmail'
            control={control}
            label='Email / Username'
            placeholder='Enter username or email'
            startAdornment={
              <FontAwesomeIcon
                icon={faEnvelope}
                fontSize={20}
                className='text-primary-skyblue'
              />
            }
            shouldUnregister={false}
            rules={USERNAME_OR_EMAIL_VALIDATIONS}
          />
        </div>
        <div className='pb-2'>
          <ControlledTextInput<FormData, 'password'>
            placeholder='Password'
            name='password'
            label='Password'
            control={control}
            shouldUnregister={false}
            rules={{ required: 'Please enter password' }}
            type={hidePassword ? 'password' : 'text'}
            defaultValue=''
            startAdornment={
              <FontAwesomeIcon
                icon={faLock}
                fontSize={20}
                className='text-primary-skyblue'
              />
            }
            endAdornment={
              <FontAwesomeIcon
                onClick={() => setHidePassword(!hidePassword)}
                icon={hidePassword ? faEyeSlash : faEye}
                fontSize={20}
                className='text-primary-gray'
              />
            }
            isPassword
          />
        </div>
        <div className='justify-start flex pb-4 lg:pb-6'>
          <Link
            to={getPathWithQueryParams(
              isAgentLogin ? '/agent-forgot-password' : '/forgot-password',
              {
                leadId,
                loanId,
                emailAddress,
                inviterAuthorizedPersonId,
                inviterAgentId,
                loanOfficerId,
              },
            )}
          >
            <span className='underline font-inter-medium text-sm lg:text-base text-reskin-primary-blue'>
              Forgot Password
            </span>
          </Link>
        </div>
        <div className='flex w-full'>
          <Button
            label='Login'
            type='submit'
            fullWidth
            rightIcon={
              <FontAwesomeIcon
                icon={faArrowUpRight}
                fontSize={16}
                className='text-white'
                data-test='loading-icon'
              />
            }
            variant='primary'
            gradientVariant='mintyfresh'
            isSubmitting={isSubmitting}
            onPress={() => {
              if (get(errors, 'submit')) {
                clearErrors('submit');
              }
            }}
          />
        </div>
      </form>
      <div className='lg:row-span-1 mt-4 lg:mt-6 flex justify-center lg:flex-none lg:justify-start'>
        <p className='font-inter-medium text-sm lg:text-base text-black'>
          Don&apos;t have an account yet?
          <Link
            to={getPathWithQueryParams(
              isAgentLogin ? '/agent-signup' : '/signup',
              {
                leadId,
                loanId,
                emailAddress,
                inviterAuthorizedPersonId,
                inviterAgentId,
                loanOfficerId,
              },
            )}
          >
            <span className='text-reskin-primary-blue underline pl-1'>
              Create Account
            </span>
          </Link>
        </p>
      </div>
    </AuthPageLayout>
  );
};

export default SignInRoute;
